import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getSocket } from '@/lib/socket'
import { useNotificationStore } from '@/lib/store/useNotificationStore'
import { playNotificationSound } from '@/lib/notificationSound'
import {
  syncSingleBooking,
  syncSingleCustomerOrder,
  syncBookingsForDate,
  syncCustomerOrdersForDate,
} from '@/lib/db/initialSync'
import { useAppStore } from '@/lib/store/useAppStore'

export function useRealtimeSync() {
  const qc = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)

  useEffect(() => {
    const socket = getSocket()

    // Request browser notification permission (requires a prior user gesture — this satisfies it)
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }

    const onBookingsChanged = async (data?: { date?: string; bookingKind?: 'reservation' | 'order' }) => {
      // Write to Dexie so useLiveQuery sees the change immediately
      const restaurantId = useAppStore.getState().restaurantId
      if (restaurantId && data?.date) {
        try {
          if (data.bookingKind === 'order') {
            await syncCustomerOrdersForDate(restaurantId, data.date)
          } else {
            await syncBookingsForDate(restaurantId, data.date)
          }
        } catch (err) {
          console.error('[socket] Failed to sync reservations/orders for date:', err)
        }
      }
      qc.invalidateQueries({ queryKey: ['floorPlanLayouts'] })
    }

    const onBookingNew = async (data: {
      guestName: string
      date: string
      time: string
      people: number
      documentId: string
      bookingKind?: 'reservation' | 'order'
    }) => {
      // Write the new booking into Dexie so useLiveQuery fires immediately
      if (data.documentId) {
        try {
          if (data.bookingKind === 'order') {
            await syncSingleCustomerOrder(data.documentId)
          } else {
            await syncSingleBooking(data.documentId)
          }
        } catch (err) {
          console.error('[socket] Failed to sync new reservation/order:', err)
        }
      }
      qc.invalidateQueries({ queryKey: ['floorPlanLayouts'] })

      // Bell badge
      addNotification(data)

      // Chime
      playNotificationSound()

      // Browser notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('New booking request', {
          body: `${data.guestName} · ${data.people} ${data.people === 1 ? 'person' : 'people'} · ${data.time}`,
          icon: '/favicon.ico',
          tag: data.documentId,
        })
      }
    }

    socket.on('bookings:changed', onBookingsChanged)
    socket.on('booking:new', onBookingNew)
    const onReconnect = () => {
      void onBookingsChanged()
    }
    socket.io.on('reconnect', onReconnect)

    return () => {
      socket.off('bookings:changed', onBookingsChanged)
      socket.off('booking:new', onBookingNew)
      socket.io.off('reconnect', onReconnect)
    }
  }, [qc, addNotification])
}
